import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Activity, AlertTriangle, CheckCircle2, DatabaseZap, RefreshCw, ShieldCheck } from "lucide-react";
import { SetupStateBadge } from "./StatusBadges";
import "./HealthRoutes.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = (token) => (token ? { Authorization: `Bearer ${token}` } : {});

const healthTone = {
  LIVE: "success",
  MOCKED: "warning",
  SETUP_REQUIRED: "warning",
  FAILING: "danger",
};

const defaultLaunchForm = {
  connector_id: "jira",
  owner_role: "Project Manager",
  credential_owner: "Ava Project Manager",
  launch_option: "Use launch credential slot when real token is provided",
  dummy_reference: "dummy-replace-before-launch",
};

export const IntegrationHealthCenter = ({ auth }) => {
  const [health, setHealth] = useState({ connectors: [], summary: {}, digital_twin_calibration: {} });
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState(defaultLaunchForm);

  const loadHealth = useCallback(async (showNotice = true) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/connectors/health`, { headers: authHeaders(auth.token) });
      setHealth(response.data);
      if (showNotice) setNotice("Integration health refreshed.");
    } catch (err) {
      setNotice("Integration health is unavailable. Please confirm your APMS session.");
    } finally {
      setLoading(false);
    }
  }, [auth.token]);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  const stageLaunchOption = async () => {
    try {
      await axios.post(`${API}/connectors/${form.connector_id}/launch-option`, form, { headers: authHeaders(auth.token) });
      setNotice(`${form.connector_id.toUpperCase()} launch option staged for Project Manager replacement.`);
      await loadHealth(false);
      setNotice(`${form.connector_id.toUpperCase()} launch option staged for Project Manager replacement.`);
    } catch (err) {
      setNotice("Launch option staging requires Admin or Project Manager access.");
    }
  };

  const grouped = useMemo(() => {
    return health.connectors.reduce((acc, connector) => {
      const key = connector.health || "SETUP_REQUIRED";
      acc[key] = [...(acc[key] || []), connector];
      return acc;
    }, {});
  }, [health.connectors]);

  const sections = [
    ["LIVE", "Live", CheckCircle2],
    ["MOCKED", "MOCKED", DatabaseZap],
    ["SETUP_REQUIRED", "Setup Required", ShieldCheck],
    ["FAILING", "Failing", AlertTriangle],
  ];

  return (
    <div className="reference-page projects-reference single" data-testid="integration-health-page">
      <section className="workflow-hero integration-health-hero" data-testid="integration-health-hero">
        <div>
          <span className="section-kicker" data-testid="integration-health-kicker"><Activity size={14} /> Launch readiness</span>
          <h2 data-testid="integration-health-title">Integration Health Center</h2>
          <p data-testid="integration-health-description">Live, MOCKED, setup-required, and failing connector states separated for stakeholder demos and launch readiness reviews.</p>
        </div>
        <div className="health-summary-grid" data-testid="integration-health-summary-grid">
          {sections.map(([key, label]) => (
            <article className={`health-summary-card tone-${healthTone[key]}`} key={key} data-testid={`health-summary-${key.toLowerCase()}`}>
              <strong data-testid={`health-summary-value-${key.toLowerCase()}`}>{health.summary?.[key] || 0}</strong>
              <span data-testid={`health-summary-label-${key.toLowerCase()}`}>{label}</span>
            </article>
          ))}
        </div>
      </section>

      {notice && <p className="notice" data-testid="integration-health-notice">{notice}</p>}

      <section className="content-grid one-one" data-testid="integration-health-actions-grid">
        <section className="ref-card settings-panel" data-testid="launch-option-panel">
          <div className="projects-tab-row">
            <h2 data-testid="launch-option-title">Project Manager credential staging</h2>
            <button className="secondary-button compact" onClick={loadHealth} data-testid="refresh-integration-health-button">{loading ? "Refreshing..." : <><RefreshCw size={14} /> Refresh</>}</button>
          </div>
          <div className="form-grid" data-testid="launch-option-form">
            <select className="select-input" value={form.connector_id} onChange={(event) => setForm({ ...form, connector_id: event.target.value })} data-testid="launch-option-connector-select">
              {health.connectors.map((connector) => <option key={connector.id} value={connector.id}>{connector.name}</option>)}
            </select>
            <input className="text-input" value={form.credential_owner} onChange={(event) => setForm({ ...form, credential_owner: event.target.value })} data-testid="launch-option-owner-input" aria-label="Credential owner" />
            <input className="text-input span-2" value={form.launch_option} onChange={(event) => setForm({ ...form, launch_option: event.target.value })} data-testid="launch-option-note-input" aria-label="Launch option note" />
            <input className="text-input span-2" value={form.dummy_reference} onChange={(event) => setForm({ ...form, dummy_reference: event.target.value })} data-testid="launch-option-dummy-reference-input" aria-label="Dummy reference" />
            <button className="primary-button" onClick={stageLaunchOption} data-testid="stage-launch-option-button">Stage replaceable launch option</button>
          </div>
        </section>

        <section className="ref-card settings-panel" data-testid="digital-twin-calibration-panel">
          <div className="projects-tab-row">
            <h2 data-testid="digital-twin-calibration-title">Digital Twin calibration</h2>
            <SetupStateBadge testId="digital-twin-awaiting-data-badge" label="AWAITING HISTORICAL DATA" />
          </div>
          <p data-testid="digital-twin-calibration-status">{health.digital_twin_calibration?.status || "Waiting for real delivery history before calibration."}</p>
          <div className="calibration-requirements" data-testid="digital-twin-calibration-requirements">
            {(health.digital_twin_calibration?.required_data || []).map((item) => <span key={item} data-testid={`digital-twin-required-${item.toLowerCase().replaceAll(" ", "-")}`}>{item}</span>)}
          </div>
        </section>
      </section>

      <section className="health-column-grid" data-testid="integration-health-columns">
        {sections.map(([key, label, Icon]) => (
          <section className="ref-card health-column" key={key} data-testid={`health-column-${key.toLowerCase()}`}>
            <div className="projects-tab-row">
              <h2 data-testid={`health-column-title-${key.toLowerCase()}`}><Icon size={20} /> {label}</h2>
              <span className="status-chip" data-testid={`health-column-count-${key.toLowerCase()}`}>{grouped[key]?.length || 0}</span>
            </div>
            <div className="approval-stack" data-testid={`health-list-${key.toLowerCase()}`}>
              {(grouped[key] || []).map((connector) => (
                <article className="approval-item setup-required-card" key={connector.id} data-testid={`health-connector-${connector.id}`}>
                  <strong data-testid={`health-connector-name-${connector.id}`}>{connector.name}</strong>
                  <p data-testid={`health-connector-status-${connector.id}`}>{connector.status}</p>
                  <span className="status-chip" data-testid={`health-connector-category-${connector.id}`}>{connector.category}</span>
                  {connector.setup_required && <SetupStateBadge testId={`health-setup-required-${connector.id}`} label={connector.health === "MOCKED" ? "MOCKED" : "SETUP REQUIRED"} />}
                  {connector.last_sync && <small data-testid={`health-connector-sync-${connector.id}`}>Last sync: {connector.last_sync}</small>}
                  {connector.launch_option && <small data-testid={`health-launch-option-${connector.id}`}>Launch option: {connector.launch_option}</small>}
                </article>
              ))}
            </div>
          </section>
        ))}
      </section>
    </div>
  );
};